// Install eligibility + suppression for the Sketchbook Peek install banner
// (`src/components/InstallBanner.vue`). Three platforms get distinct treatment:
//
//   - 'native-prompt'  Android Chrome / Edge / desktop Chromium where
//                      `beforeinstallprompt` will fire — the banner ends in a
//                      programmatic Install button.
//   - 'ios-safari'     The only iOS browser that supports Add to Home Screen.
//                      Banner ends in a Tap-Share-then-Add-to-Home-Screen
//                      walkthrough since Apple offers no JS install path.
//   - 'unsupported'    Anywhere else (iOS Chrome / Firefox / Edge / Opera,
//                      iOS in-app webviews like Instagram / Facebook / Line,
//                      Firefox desktop, etc.) — banner stays hidden because
//                      we have no way to fulfil the ask there. Showing it
//                      would be untappable misdirection.
//
// `beforeinstallprompt` can fire BEFORE Vue lazy-mounts InstallBanner.vue, so
// `initInstallTracking()` is called from main.js to install window listeners
// at app boot. The deferred prompt is cached for any subscriber that registers
// later; that's what makes lazy-loading the banner safe.

let cachedDeferredPrompt = null;
const promptSubscribers = new Set();
const installedSubscribers = new Set();
let initialized = false;

const STORAGE_KEY = "scrawl:install-banner";
const DAY_MS = 24 * 60 * 60 * 1000;

// Polite, not punitive. Calibrated against the project's existing dismiss-once
// banner: a single × there nuked the prompt for the rest of the session and
// then re-pestered on every reload — these two windows give a softer middle.
const PEEK_DISMISS_DAYS = 7;   // × on the collapsed peek strip
const DEEP_DISMISS_DAYS = 14;  // "Maybe later" / "I'll do it myself" inside the sheet

// ─── PLATFORM DETECTION ────────────────────────────────────────────────────

// True when the page is running in any installed-PWA context: iOS legacy
// `navigator.standalone`, modern `display-mode: standalone`, Android TWA
// (`minimal-ui`), and `fullscreen` (rare but spec-defined).
export function isStandalone() {
  if (typeof window === "undefined") return false;
  if (window.navigator && window.navigator.standalone === true) return true;
  if (typeof window.matchMedia !== "function") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: minimal-ui)").matches ||
    window.matchMedia("(display-mode: fullscreen)").matches
  );
}

// True on iPhone, iPod, iPad, and iPadOS 13+ desktop-mode Safari (which
// reports as Macintosh in the UA — distinguish via `maxTouchPoints > 1`,
// which a real Mac trackpad does not report).
export function isIOSDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  const isMacUA = /Macintosh/.test(ua);
  const hasTouch =
    typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1;
  return isMacUA && hasTouch;
}

// True only when running in iOS Safari — never in iOS Chrome (CriOS),
// Firefox (FxiOS), Edge (EdgiOS), Opera (OPiOS), Yandex (YaBrowser), the
// Google Search App (GSA), or in-app webviews used by Instagram / Facebook
// (FBAN/FBAV/FBIOS) / Line / Outlook. All of those use Apple's WebKit but
// have no Add to Home Screen affordance — showing the banner there would
// be a dead-end.
export function isIOSSafari() {
  if (!isIOSDevice()) return false;
  const ua = navigator.userAgent || "";
  if (
    /CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser|GSA|FBAN|FBAV|FBIOS|Instagram|Line\//.test(
      ua
    )
  ) {
    return false;
  }
  // True Safari has both the WebKit engine token and the Safari/<version>
  // brand token. WKWebView-only contexts (some other in-app browsers) lack
  // the latter and fall through.
  return /AppleWebKit/.test(ua) && /Safari\//.test(ua);
}

// True when running in Chrome on an iOS device (CriOS UA token).
// Chrome on iOS 16.4+ (Chrome 116+) supports PWA installation via
// ⋮ → Add to Home Screen. Checked before isIOSSafari so these users
// get Chrome-specific instructions rather than falling into 'unsupported'.
export function isIOSChrome() {
  if (!isIOSDevice()) return false;
  return /CriOS/.test(navigator.userAgent || "");
}

// Categorical platform classification used by InstallBanner.vue to decide
// which card set to render and what the final CTA does. Reactive: if a
// `beforeinstallprompt` event fires later, the banner upgrades to
// 'native-prompt' regardless of what this returned at mount.
export function detectInstallPlatform() {
  if (isStandalone()) return "standalone";
  if (cachedDeferredPrompt) return "native-prompt";
  if (isIOSChrome()) return "ios-chrome";
  if (isIOSSafari()) return "ios-safari";
  return "unsupported";
}

// ─── DEFERRED PROMPT CACHE ─────────────────────────────────────────────────

// Idempotent. Called once from main.js. Without this, `beforeinstallprompt`
// fires during the gap between page load and Vue resolving the lazy
// InstallBanner import, and we lose the only chance to capture the event.
export function initInstallTracking() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  window.addEventListener("beforeinstallprompt", (event) => {
    // Suppress the browser's native mini-infobar — we own the install UX.
    event.preventDefault();
    cachedDeferredPrompt = event;
    promptSubscribers.forEach((fn) => {
      try { fn(event); } catch { /* one bad listener mustn't break the rest */ }
    });
  });

  window.addEventListener("appinstalled", () => {
    cachedDeferredPrompt = null;
    recordInstalled();
    installedSubscribers.forEach((fn) => {
      try { fn(); } catch { /* same */ }
    });
  });
}

export function getDeferredPrompt() {
  return cachedDeferredPrompt;
}

export function clearDeferredPrompt() {
  cachedDeferredPrompt = null;
}

// `prompt()` consumes the event — once dispatched it can't be re-shown, so
// the cache must be cleared after a successful prompt() call regardless of
// the user's choice.
export function consumeDeferredPrompt() {
  const event = cachedDeferredPrompt;
  cachedDeferredPrompt = null;
  return event;
}

export function onDeferredPrompt(fn) {
  promptSubscribers.add(fn);
  // Late subscriber may have missed the event — deliver immediately if cached.
  if (cachedDeferredPrompt) {
    try { fn(cachedDeferredPrompt); } catch { /* ignore */ }
  }
  return () => promptSubscribers.delete(fn);
}

export function onAppInstalled(fn) {
  installedSubscribers.add(fn);
  return () => installedSubscribers.delete(fn);
}

// ─── SUPPRESSION (localStorage) ────────────────────────────────────────────

function readState() {
  if (typeof localStorage === "undefined") return {};
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function writeState(state) {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Private mode / quota exceeded — silently degrade. Worst case the banner
    // re-shows on next visit, which is the same "ask again" cadence the old
    // single-dismiss banner had anyway.
  }
}

export function shouldShowBanner() {
  if (isStandalone()) return false;
  const state = readState();
  if (state.installed) return false;
  if (state.suppressUntil && Date.now() < state.suppressUntil) return false;
  return true;
}

export function recordPeekDismiss() {
  const state = readState();
  state.peekDismissCount = (state.peekDismissCount || 0) + 1;
  state.suppressUntil = Date.now() + PEEK_DISMISS_DAYS * DAY_MS;
  writeState(state);
}

export function recordDeepDismiss() {
  const state = readState();
  state.deepDismissCount = (state.deepDismissCount || 0) + 1;
  state.suppressUntil = Date.now() + DEEP_DISMISS_DAYS * DAY_MS;
  writeState(state);
}

export function recordInstalled() {
  writeState({ installed: true, installedAt: Date.now() });
}

// Test seam — not used at runtime. Lets a manual tester clear suppression
// from devtools without touching localStorage directly:
//   `import('./utils/installEligibility').then(m => m.clearSuppression())`
export function clearSuppression() {
  writeState({});
}
